import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type {
  ApiResponse,
  GroupOperatingFunds,
  GroupOperatingFundsUpsertPayload,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';

/**
 * Umbral de fondo operativo de grupo.
 *
 *   - GET `/api/structure/groups/{groupId}/operating-funds`
 *   - PUT `/api/structure/groups/{groupId}/operating-funds` (multipart)
 */
@Injectable({ providedIn: 'root' })
export class GroupOperatingFundsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/groups`;
  }

  get(groupId: string): Observable<GroupOperatingFunds> {
    return this.httpClient
      .get<ApiResponse<GroupOperatingFunds | null>>(
        `${this.apiUrl}/${groupId}/operating-funds`,
      )
      .pipe(map((response) => this.mapItem(response.data, groupId)));
  }

  update(
    groupId: string,
    payload: GroupOperatingFundsUpsertPayload,
  ): Observable<GroupOperatingFunds> {
    return this.httpClient
      .put<ApiResponse<GroupOperatingFunds | null>>(
        `${this.apiUrl}/${groupId}/operating-funds`,
        this.toFormData(payload),
      )
      .pipe(map((response) => this.mapItem(response.data, groupId)));
  }

  private toFormData(payload: GroupOperatingFundsUpsertPayload): FormData {
    const formData = new FormData();

    if (payload.mandatory != null) {
      formData.append('mandatory', String(payload.mandatory));
    }
    if (payload.restrictive != null) {
      formData.append('restrictive', String(payload.restrictive));
    }
    if (payload.amount != null && payload.amount !== '') {
      formData.append('amount', String(payload.amount));
    }
    if (payload.observations != null && payload.observations !== '') {
      formData.append('observations', payload.observations);
    }

    formData.append('coinId', payload.coinId);

    const files: File[] = payload.files ?? [];
    const orders: number[] = payload.orders ?? [];
    files.forEach((file, index) => {
      formData.append('files', file);
      formData.append('orders', String(orders[index] ?? index + 1));
    });

    return formData;
  }

  private mapItem(
    data: GroupOperatingFunds | null | undefined,
    groupId: string,
  ): GroupOperatingFunds {
    return {
      groupId: data?.groupId || groupId,
      mandatory: data?.mandatory,
      restrictive: data?.restrictive,
      amount: data?.amount ?? null,
      observations: data?.observations ?? null,
      coinId: data?.coinId ?? null,
      files: Array.isArray(data?.files) ? data.files : [],
    };
  }
}
